import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, User, Mail, Lock, Phone, Building } from 'lucide-react';
import { toast } from 'sonner';

const PartnerRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDSA = location.pathname === '/dsa-register';
  const partnerType = isDSA ? 'dsa' : 'referral_partner';
  const title = isDSA ? 'DSA Registration' : 'Referral Partner Registration';
  const color = isDSA ? 'orange' : 'navy';

  const [formData, setFormData] = useState({
    email: '', password: '', name: '', mobile: '',
    city: '', district: '', state: '',
    pan: '', gst: '',
    bank_account_name: '', bank_account_number: '', bank_ifsc: '',
    referred_by: ''
  });
  const [loading, setLoading] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/partners/register`,
        { ...formData, partner_type: partnerType },
        { withCredentials: true }
      );
      toast.success(`Registration successful! Your referral code: ${data.referral_code}`);
      navigate('/partner-dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-ring";

  return (
    <div className="min-h-screen bg-background py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Sparkles className="h-10 w-10 text-gold" />
            <div>
              <div className="text-2xl font-bold text-navy">ProcureOne Ultra</div>
              <div className="text-xs text-muted-foreground">by Plus Ultra Innovation</div>
            </div>
          </div>
          <h1 className="text-3xl font-semibold text-navy">{title}</h1>
          <p className="text-muted-foreground mt-2">
            {isDSA ? 'Earn 15% commission on every conversion' : 'Earn 10% commission on every conversion'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-8 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            <h2 className="text-lg font-semibold text-navy">Account Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputCls} data-testid="partner-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} data-testid="partner-email-input" />
                <p className="text-xs text-muted-foreground mt-1">Used for commission statements & invoices.</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Mobile *</label>
                <input type="tel" required value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} className={inputCls} data-testid="partner-mobile-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password *</label>
                <input type="password" required minLength={6} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className={inputCls} data-testid="partner-password-input" />
              </div>
            </div>

            <h2 className="text-lg font-semibold text-navy mt-6">Location</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <input type="text" placeholder="City" required value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className={inputCls} data-testid="partner-city-input" />
              <input type="text" placeholder="District" value={formData.district} onChange={(e) => setFormData({ ...formData, district: e.target.value })} className={inputCls} data-testid="partner-district-input" />
              <input type="text" placeholder="State" required value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className={inputCls} data-testid="partner-state-input" />
            </div>

            <h2 className="text-lg font-semibold text-navy mt-6">Tax & Banking (for commission payouts)</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="PAN" value={formData.pan} onChange={(e) => setFormData({ ...formData, pan: e.target.value })} className={inputCls} data-testid="partner-pan-input" />
              <input type="text" placeholder="GST (optional)" value={formData.gst} onChange={(e) => setFormData({ ...formData, gst: e.target.value })} className={inputCls} data-testid="partner-gst-input" />
            </div>
            <input type="text" placeholder="Bank Account Holder Name" value={formData.bank_account_name} onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })} className={inputCls} data-testid="partner-bankname-input" />
            <div className="grid md:grid-cols-2 gap-4">
              <input type="text" placeholder="Bank Account Number" value={formData.bank_account_number} onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })} className={inputCls} data-testid="partner-accountno-input" />
              <input type="text" placeholder="IFSC Code" value={formData.bank_ifsc} onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })} className={inputCls} data-testid="partner-ifsc-input" />
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium mb-2">Referred By (optional)</label>
              <input type="text" placeholder="Enter referral code if any" value={formData.referred_by} onChange={(e) => setFormData({ ...formData, referred_by: e.target.value.toUpperCase() })} className={inputCls} data-testid="partner-referredby-input" />
            </div>

            <button type="submit" disabled={loading} className={`w-full px-6 py-3 rounded-md text-white font-semibold mt-6 ${isDSA ? 'bg-orange' : 'bg-navy'} hover:opacity-90`} data-testid="partner-submit-btn">
              {loading ? 'Registering...' : `Register as ${isDSA ? 'DSA' : 'Referral Partner'}`}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-navy font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerRegister;
